import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Collapse,
  Box,
  Typography,
  TableSortLabel,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useNavigate } from "react-router-dom";
import DeleteConfirmation from "../common/DeleteConfirmation";
import { NotificationManager } from "react-notifications";
import { DeleteInvoice } from "../../services/InvoiceService";
import InvoiceTransactionsTable from "./InvoiceTransactionsTable";
import {
  getComparator,
  stableSort,
} from "../../../common-utilities/sortingUtils";

const InvoicesTable = ({ invoicesData }) => {
  const columns = [
    "Invoice Number",
    "Date Invoiced",
    "Invoice Amount",
    "Account Number",
    "Invoice Status",
    "Order Number",
    "Actions",
  ];
  const columnHeaders = [
    "invoiceNumber",
    "dateInvoiced",
    "invoiceAmount",
    "accountNumber",
    "invoiceStatus",
    "orderNumber",
  ];
  const navigate = useNavigate();
  const rowsPerPageOptions = [20, 50, 100, 150, 200, 400, 600, 800, 1000];

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [selectedInvoiceToDelete, setSelectedInvoiceToDelete] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");

  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "2-digit", day: "2-digit" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleViewClick = (invoiceNumber) => {
    const orderUrl = `/vlp/invoices/${invoiceNumber}`;

    if (window.invoiceViewWindow && !window.invoiceViewWindow.closed) {
      window.invoiceViewWindow.location.href = orderUrl;
    } else {
      window.invoiceViewWindow = window.open(
        orderUrl,
        "_blank",
        "resizable=yes,scrollbars=yes"
      );
    }
  };

  // const handleEditClick = (invoiceNumber) => {
  //   navigate("/vlp/invoices/edit/"+invoiceNumber);
  // };
  const handleEditClick = (invoiceNumber) => {
    window.open(`/vlp/invoices/edit/${invoiceNumber}`, "_blank");
  };


  const handleDeleteClick = (invoiceNumber) => {
    setSelectedInvoiceToDelete(invoiceNumber);
    setIsDeleteConfirmationOpen(true);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      const response = await DeleteInvoice(selectedInvoiceToDelete);
      if (response.results === true) {
        NotificationManager.success("Invoice deleted successfully");
        setIsDeleteConfirmationOpen(false);
        // Optionally, refresh the data or update state after deletion
        // For example: fetchInvoices();
        window.location.reload();
      } else {
        NotificationManager.error("Error deleting invoice");
      }
    } catch (error) {
      NotificationManager.error("Error deleting invoice");
    }
  };

  const renderActions = (invoiceNumber) => (
    <div style={{ minWidth: "120px" }}>
      <IconButton
        aria-label="edit"
        style={{ color: "orange" }}
        onClick={() => handleEditClick(invoiceNumber)}
      >
        <EditIcon />
      </IconButton>
      <IconButton
        aria-label="view"
        style={{ color: "green" }}
        onClick={() => handleViewClick(invoiceNumber)}
      >
        <VisibilityIcon />
      </IconButton>
      <IconButton
        aria-label="delete"
        style={{ color: "red" }}
        onClick={() => handleDeleteClick(invoiceNumber)}
      >
        <DeleteIcon />
      </IconButton>
    </div>
  );

  const sortedData = stableSort(invoicesData, getComparator(order, orderBy));

  const Row = ({ row, index }) => {
    const [open, setOpen] = useState(false);

    return (
      <React.Fragment>
        <TableRow
          sx={{
            "& > *": { borderBottom: "unset" },
            backgroundColor:
              index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
          }}
        >
          <TableCell>
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
          {columnHeaders.map((column) => (
            <TableCell key={column}>
              {column === "dateInvoiced"
                ? formatDate(row[column])
                : row[column]}
            </TableCell>
          ))}
          <TableCell>{renderActions(row.invoiceNumber)}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell
            style={{ paddingBottom: 0, paddingTop: 0 }}
            colSpan={columns.length + 1}
          >
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1 }}>
                {row !== null && (
                  <InvoiceTransactionsTable
                    transactions={row.transactions}
                    orderNumber={row.orderNumber}
                  />
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  return (
    <>
      <Paper>
        <TableContainer
          sx={{ maxHeight: 500, overflowY: "auto", width: "99%" }}
        >
          <Table sx={{ minWidth: 700 }} stickyHeader>
            <TableHead
              sx={{
                backgroundColor: (theme) => theme.palette.primary.main,
                color: "white",
              }}
            >
              <TableRow>
                <TableCell />
                {columns.map((column) => (
                  <TableCell key={column}>
                    <TableSortLabel
                      active={
                        orderBy === columnHeaders[columns.indexOf(column)]
                      }
                      direction={
                        orderBy === columnHeaders[columns.indexOf(column)]
                          ? order
                          : "asc"
                      }
                      onClick={() =>
                        handleRequestSort(
                          columnHeaders[columns.indexOf(column)]
                        )
                      }
                    >
                      {column}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.slice(startIndex, endIndex).map((order, index) => (
                <Row key={order.orderNumber} row={order} index={index} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={invoicesData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onConfirm={handleDeleteConfirmationConfirm}
        title="Delete Confirmation"
        message="Are you sure you want to delete this invoice?"
        color="red"
      />
    </>
  );
};

export default InvoicesTable;
